import { TZDateMini } from '@date-fns/tz';
import { format } from 'date-fns/format';
import type { ReactNode } from 'react';
import type { PopupProps } from 'semantic-ui-react';
import { Popup } from 'semantic-ui-react';

import { formatRelativeDate } from '@utils/relative-date';

export interface RelativeTimestampProps {
  date: string | number;
  includeUTC?: boolean;
  popupPosition?: PopupProps['position'];
  /**
   * Optional Popper offset for the hover popup. Useful when the trigger
   * sits inside a bordered container (like an `InfoChip`) and the popup
   * arrow would otherwise clip the border. See `LIFTED_POPUP_OFFSET` in
   * `@utils/popup` for the standard value.
   */
  popupOffset?: PopupProps['offset'];
  /**
   * Capitalize the first letter of the relative date (e.g. "About 1 hour ago").
   */
  capitalize?: boolean;
  /**
   * Optional custom popup trigger; when provided, replaces the default inline span.
   */
  children?: ReactNode;
}

/**
 * A reusable relative timestamp which shows a formatted date on hover
 *
 * @param date Date string or UNIX timestamp to format
 * @param includeUTC Whether to show UTC time in popup
 * @param popupPosition Position of the popup
 * @param popupOffset Optional Popper offset for the popup
 * @param capitalize Capitalize the first letter of the relative date
 *
 * @example
 * <RelativeTimestamp date="2023-09-01T00:00:00Z" />
 * <RelativeTimestamp date="2023-09-01T00:00:00Z" includeUTC />
 * <RelativeTimestamp date={1693526400} capitalize />
 */
const RelativeTimestamp = ({
  date,
  includeUTC = false,
  popupPosition = 'top center',
  popupOffset,
  capitalize = false,
  children,
}: RelativeTimestampProps) => {
  // Handle both ISO strings and UNIX timestamps
  const dateValue =
    typeof date === 'number'
      ? new Date(date * 1000) // Convert UNIX timestamp to Date
      : new Date(date); // Convert ISO string to Date

  const utcDate = new TZDateMini(dateValue, 'UTC');
  const localDate = new TZDateMini(utcDate);
  const relativeDate = formatRelativeDate(date, { capitalize });
  const preciseUTCDate = format(utcDate, 'PPpp');
  const preciseLocalDate = format(localDate, 'PPpp');

  return (
    <Popup
      trigger={children ?? <span>{relativeDate}</span>}
      position={popupPosition}
      offset={popupOffset}
      content={
        <>
          <div>{preciseLocalDate}</div>
          {includeUTC && (
            <div>
              <em>{preciseUTCDate} UTC</em>
            </div>
          )}
        </>
      }
    />
  );
};

export default RelativeTimestamp;
